import styleIcon from 'uiComponents/studio/icons/style.svg'
import copyIcon from 'uiComponents/studio/icons/copy.svg'
import restrictionsIcon from 'uiComponents/studio/icons/target.svg'
import settingsIcon from 'uiComponents/studio/icons/settings.svg'
import { SectionObject } from 'uiComponents/studio/menuItem'
import { t } from 'translations'
import { parsePixels } from 'utils/css'
import { fontOptions } from '../shared'

export interface FormDefinitionSchema {
    text: SectionObject
    style: SectionObject
    targeting: SectionObject
    settings: SectionObject
}

const px = {
    render: (val: number) => (val === 0 ? '0' : val + 'px'),
    parse: parsePixels,
}

export const getWonderBarFormDefinition = (): FormDefinitionSchema => ({
    text: {
        name: 'text',
        label: t('engageTools.studio.config.steps.text', { step: 1 }),
        header: t('engageTools.studio.config.headers.createMessage'),
        icon: copyIcon,
        description: t('engageTools.studio.wonderbar.descriptions.text'),
        sections: [
            {
                name: 'Languages',
                label: t('engageTools.studio.config.sections.languages'),
                fields: [],
            },
        ],
    },
    style: {
        name: 'style',
        label: t('engageTools.studio.config.steps.style', { step: 2 }),
        header: t('engageTools.studio.config.headers.designLookAndFeel'),
        icon: styleIcon,
        description: t('engageTools.studio.wonderbar.descriptions.style'),
        sections: [
            {
                name: t('engageTools.studio.config.sections.general'),
                fields: [
                    {
                        name: 'size-template',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.normal'),
                                value: 'normal',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.wonderbarSize'),
                        span: 6,
                        path: 'styling.sizeTemplate',
                        defaultValue: 'normal',
                    },
                    {
                        name: 'text-font',
                        type: 'select',
                        availableOptions: [...fontOptions],
                        label: t('engageTools.studio.config.fields.fontFamily'),
                        span: 6,
                        path: 'styling.fontFamily',
                        defaultValue: '',
                    },
                    {
                        name: 'text-font-size',
                        type: 'number',
                        min: 9,
                        max: 24,
                        unit: 'px',
                        label: t('engageTools.studio.config.fields.textSize'),
                        span: 6,
                        path: 'styling.fontSize',
                        defaultValue: 15,
                        converter: px,
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.barStyling'),
                fields: [
                    {
                        name: 'bar-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.barColor'),
                        span: 6,
                        path: 'styling.bar.background',
                        defaultValue: '#FFFFFF',
                    },
                    {
                        name: 'bar-text-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.textColor'),
                        span: 6,
                        path: 'styling.bar.textColor',
                        defaultValue: '#999999',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.buttonStyling'),
                fields: [
                    {
                        name: 'button-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.buttonColor'),
                        span: 6,
                        path: 'styling.cta.background',
                        defaultValue: '#CCCCCC',
                    },
                    {
                        name: 'border-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.borderColor'),
                        span: 6,
                        path: 'styling.cta.borderColor',
                        defaultValue: '#FFFFFF',
                    },
                    {
                        name: 'button-text-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.textColor'),
                        span: 6,
                        path: 'styling.cta.textColor',
                        defaultValue: '#999999',
                    },
                    {
                        name: 'button-corner',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.square'),
                                value: '0px',
                            },
                            {
                                name: t('engageTools.studio.config.options.slightlyRounded'),
                                value: '6px',
                            },
                            {
                                name: t('engageTools.studio.config.options.rounded'),
                                value: '10px',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.buttonCorners'),
                        span: 6,
                        path: 'styling.cta.borderRadius',
                        defaultValue: '6x',
                    },
                    {
                        name: 'border-width',
                        type: 'number',
                        min: 0,
                        max: 5,
                        unit: 'px',
                        label: t('engageTools.studio.config.fields.borderWidth'),
                        span: 6,
                        path: 'styling.cta.borderWidth',
                        defaultValue: 1,
                        converter: px,
                    },
                ],
            },
        ],
    },
    targeting: {
        name: 'targeting',
        label: t('engageTools.studio.config.steps.targeting', { step: 3 }),
        header: t('engageTools.studio.config.headers.setRestrictions'),
        icon: restrictionsIcon,
        description: t('engageTools.studio.wonderbar.descriptions.targeting'),
        sections: [
            {
                name: 'Audience',
                fields: [],
            },
            {
                name: t('engageTools.studio.config.sections.displayOn'),
                infotip: t('engageTools.studio.wonderbar.infotips.displayOn'),
                fields: [
                    {
                        name: 'display',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.allDevices'),
                                value: 'all',
                            },
                            {
                                name: t('engageTools.studio.config.options.onlyMobile'),
                                value: 'mobile',
                            },
                            {
                                name: t('engageTools.studio.config.options.onlyDesktop'),
                                value: 'desktop',
                            },
                        ],
                        label: '',
                        span: 12,
                        path: '',
                        defaultValue: 'all',
                    },
                ],
            },
            {
                name: 'Blacklist',
                label: t('engageTools.studio.wonderbar.blacklistLabel'),
                infotip: t('engageTools.studio.wonderbar.infotips.blacklist'),
                fields: [],
            },
        ],
    },
    settings: {
        name: 'settings',
        label: t('engageTools.studio.config.steps.settings', { step: 4 }),
        header: t('engageTools.studio.config.headers.setVisibilityRules'),
        icon: settingsIcon,
        description: t('engageTools.studio.wonderbar.descriptions.settings'),
        sections: [
            {
                name: t('engageTools.studio.config.sections.placement'),
                fields: [
                    {
                        name: 'placement',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.top'),
                                value: 'top',
                            },
                            {
                                name: t('engageTools.studio.config.options.bottom'),
                                value: 'bottom',
                            },
                        ],
                        label: t('engageTools.studio.config.sections.displayOn'),
                        span: 6,
                        premium: true,
                        path: 'placement',
                        defaultValue: 'top',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.appearance'),
                fields: [
                    {
                        name: 'display-after',
                        type: 'number',
                        min: 0,
                        max: 60,
                        unit: 'sec',
                        label: t('engageTools.studio.config.fields.displayAfter'),
                        span: 6,
                        path: 'showDelay',
                        defaultValue: 5,
                        infotip: t('engageTools.studio.wonderbar.infotips.displayAfter'),
                    },
                    {
                        name: 'visible-on-scroll',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.visibleOnScroll'),
                        span: 6,
                        path: 'visibleOnScroll',
                        defaultValue: false,
                        infotip: t('engageTools.studio.wonderbar.infotips.visibleOnScroll'), // tslint:disable-line
                    },
                    {
                        name: 'push-down',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.pushPageDown'),
                        span: 6,
                        path: 'pushDown',
                        defaultValue: true,
                        infotip: t('engageTools.studio.wonderbar.infotips.pushDown'),
                    },
                    {
                        name: 'disable-fixed-elements-push-down',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.disablePushFixedElements'),
                        span: 6,
                        path: 'disablePushFixedElements',
                        defaultValue: false,
                        infotip: t('engageTools.studio.wonderbar.infotips.disablePushFixedElements'),
                    },
                    {
                        name: 'allow-user-close',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.allowUsersToClose'),
                        span: 6,
                        path: 'showCloseBtn',
                        defaultValue: true,
                        infotip: t('engageTools.studio.wonderbar.infotips.allowUserClose'),
                    },
                    // {
                    //   name: 'convious-logo',
                    //   type: 'toggle',
                    //   label: 'Convious Logo',
                    //   span: 6,
                    //   premium: true,
                    //   path: 'enableConviousLogo',
                    //   defaultValue: true,
                    //   infotip: 'When enabled, Convious logo will be displayed in the top left corner of your WonderBar.'
                    // }
                ],
            },
            {
                name: t('engageTools.studio.config.sections.animation'),
                fields: [
                    {
                        name: 'animate-on-entry-exit',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.animateOnEntryExit'),
                        span: 6,
                        path: 'animation.enableOnEntryExit',
                        defaultValue: true,
                        infotip: t('engageTools.studio.wonderbar.infotips.animateOnEntryExit'),
                    },
                    {
                        name: 'pulse-button',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.pulseButton'),
                        span: 6,
                        path: 'animation.pulseButton',
                        defaultValue: false,
                        infotip: t('engageTools.studio.wonderbar.infotips.pulseButton'),
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.displayDelayAfterClick'),
                fields: [
                    {
                        name: 'display-delay',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.alwaysDisplay'),
                                value: '0',
                            },
                            {
                                name: t('engageTools.studio.config.durations.day'),
                                value: '86400',
                            },
                            {
                                name: t('engageTools.studio.config.durations.week'),
                                value: '604800',
                            },
                            {
                                name: t('engageTools.studio.config.durations.month'),
                                value: '18144000',
                            },
                        ],
                        label: t('engageTools.studio.wonderbar.displayDelayLabel'),
                        span: 12,
                        path: 'throttling.cooldown',
                        defaultValue: '1 day',
                    },
                ],
            },
        ],
    },
})
